import { GetArticleCategoryDto, UserDto } from 'generated/article-backend';
import { RequestDto, RequestListBuilderFilter } from 'generated/request-backend';
import create, { SetState, StateCreator } from 'zustand';
import { SearchInField } from 'app/models/common/enumerators';
import { persist } from 'zustand/middleware';
import { AccountInfo } from '@azure/msal-browser';

interface ILookUpProps {
    key: number;
    column: string;
    isChecked: boolean;
}

interface IBoxChecked {
    all: boolean,
    new: boolean,
    active: boolean,
    resolved: boolean
}

export interface IStore {
    // Store variables
    projectName: string,
    requestTitle: string,
    requestDescription: string,
    recentlyAddedData: string[],
    postCommentsData: Object[],
    redirectRequestType: string,
    redirectSubType?: number,
    impactedRevenue: string,
    impactedDevices: string,
    successPopUp: boolean,
    count: {},
    requestType: string,
    quickLinksValue?: number,
    accessToken: string,
    userDetails: UserDto,
    msGraphAccessToken: string,
    accountDetails: AccountInfo,
    selectedTag: {},
    userSearchValue: string,
    adminTicketsSearchValue: string,
    adminArticlesSearch: string,
    isAdvancedSearch: boolean,
    searchField: 'Default' | 'TitleOnly',
    searchInFields: SearchInField[],
    tagSearchType?: string,
    originalRoutingML?: boolean,
    // Admin
    isBoxchecked: IBoxChecked,
    lastSelectedRequest: RequestDto,
    isListView: boolean, 
    requestData: RequestDto[],
    selectionData: {},
    page: number,
    selectedTab: string,
    triageModalSuccess: boolean,
    triageModalSuccessNewModel: boolean,
    view: string,
    card: string,
    newlyAddedRow: string,
    reloadData: boolean,
    attachedFiles: Object[],
    visualFlowData: {},
    visibleColumns: Array<number>,
    columnOrder: Array<number>,
    columnsLookUpData: ILookUpProps[],
    searchOrigin: string,
    articleCategories: Array<GetArticleCategoryDto>,
    searchValueClear: boolean,
    cardTypeFilter: number,
    quickLinksFLow?: number,
    quickLinksID?: number,
    showNavigationPrompt: boolean,
    previousLocation: string,
    listBuilderFilters?: RequestListBuilderFilter,
    filterPanelDisabled: boolean,
    listIcon: string,
    listId: string | number,
    editArticleId?: Object,
    // Set Functions
    setNewlyAddedRow: (req: string) => void,
    // addRequestTitle: (req: any) => void,
    // addRequestDescription: (req: any) => void,
    // addRecentData: (req: any) => void,
    // addRevenueData: (req: any) => void,
    // addDevicesData: (req: any) => void,
    // addCommentsData: (req: any) => void,
    addFiles: (file: Object[]) => void,
    clearFiles: () => void,
    // newCommentsData: (req: any) => void,
    addSuccessPopUp: (req: boolean) => void,
    // addCount: (c1: any, c2: any, c3: any) => void,
    setRequestData: (req: RequestDto[]) => void,
    setQuickLinksFLow: (value: number | null) => void,
    setRequestType: (req: string) => void,
    setRedirectRequestType: (req: string) => void,
    setRedirectSubType: (req: number) => void,
    // setSelectionData: (val: any) => void,
    // addTriageModalSuccess: (req: any) => void,
    addTriageModalSuccessNewModel: (req: boolean) => void,
    changeView: (req: string) => void,
    changeCard: (req: string) => void,
    setAccessToken: (token: string) => void,
    setUserInfo: (userInfo: UserDto) => void,
    setMSGraphAccessToken: (token: string) => void,
    setAccountDetails: (account: AccountInfo) => void,
    // setReloadData: (req: any) => void,
    setQuickLinksValue: (val: number | null) => void,
    setQuickLinksID: (val: number | null) => void,
    setVisualFlowData: (req: RequestDto) => void,
    setUserSearchValue: (value: string) => void,
    setAdminTicketsSearchValue: (value: string) => void,
    setAdminArticlesSearch: (value: string) => void,
    setIsAdvancedSearch: (val: boolean) => void,
    setSearchField: (val: 'Default' | 'TitleOnly') => void,
    setSearchInFields: (val: SearchInField[]) => void,
    setTagSearchType: (val: string | null) => void,
    setVisibleColumns: (value: number) => void,
    setColumnOrder: (arr: Array<number>) => void,
    setColumnsLookUp: (arr: Array<ILookUpProps>) => void,
    setSelectedTag: (value: Object) => void,
    setEditArticleId: (value: Object) => void,
    setPage: (value: number) => void,
    setArticleCategories: (value: GetArticleCategoryDto[]) => void,
    setSelectedTab: (val: string) => void,
    setSearchOrigin: (val: string) => void,
    setSearchValueClear: (val: boolean) => void,
    setCardTypeFilter: (val: number) => void,
    setToInitialLookup: () => void,
    setIsListView: (val: boolean) => void,
    setLastSelectedRequest: (val: RequestDto) => void,
    // setBoxChange: (val: any) => void,
    setShowNavigationPrompt: (val: boolean) => void,
    setOriginalRoutingML : (val: boolean) => void,
    setPreviousLocation: (val: string) => void,
    setListBuilderFilters: (val: RequestListBuilderFilter) => void,
    setFilterPanelDisabled: (val: boolean) => void,
    setListIcon: (val: string) => void,
    setListId: (val: string | number) => void
}

let store: StateCreator<IStore> = (set: SetState<IStore>) => ({
    //Store variables
    projectName: 'Surface Knowledge Portal',
    requestTitle: '',
    requestDescription: '',
    recentlyAddedData: [' '],
    postCommentsData: [] as Object[],
    redirectRequestType: '',
    redirectSubType: null as number | null,
    impactedRevenue: '',
    impactedDevices: '',
    successPopUp: false,
    count: {},
    requestType: 'MANAGE_REQUEST',
    quickLinksValue: null as number | null,
    accessToken: '',
    userDetails: null, // <--- Used to be {} before IUSerDetail typing set. Watch for this.
    msGraphAccessToken: '',
    accountDetails: {} as AccountInfo,
    selectedTag: {},
    userSearchValue: '',
    adminTicketsSearchValue: '',
    adminArticlesSearch: '',
    isAdvancedSearch: true,
    searchField: 'Default' as 'Default' | 'TitleOnly',
    searchInFields: [SearchInField.AllFields] as SearchInField[],
    tagSearchType: null as string | null,
    originalRoutingML : null as boolean,
    editArticleId: null as Object,
    //admin
    isBoxchecked: { all: false, new: true, active: false, resolved: false },
    lastSelectedRequest: {} as RequestDto,
    isListView: true,
    requestData: [] as RequestDto[],
    selectionData: {},
    page: 1,
    selectedTab: '0',
    triageModalSuccess: false,
    triageModalSuccessNewModel: false,
    view: 'listView',
    card: 'manageMy',
    newlyAddedRow: '',
    reloadData: false,
    attachedFiles: [] as Object[],
    visualFlowData: {},
    visibleColumns: [] as Array<number>,
    columnOrder: [] as Array<number>,
    columnsLookUpData: [
        {
            key: 0,
            column: 'Team',
            isChecked: true
        },
        {
            key: 1,
            column: 'ID',
            isChecked: true
        },
        {
            key: 2,
            column: 'Title',
            isChecked: true
        },
        {
            key: 3,
            column: 'Request Type',
            isChecked: true
        },
        {
            key: 4,
            column: 'Status',
            isChecked: true
        },
        {
            key: 5,
            column: 'Created On',
            isChecked: true
        },
        {
            key: 6,
            column: 'Aging',
            isChecked: true
        },
        {
            key: 7,
            column: 'Total Revenue Risk',
            isChecked: true
        }
    ],
    searchOrigin: '',
    articleCategories: [] as Array<GetArticleCategoryDto>,
    searchValueClear: true,
    cardTypeFilter: 0,
    quickLinksFLow: null as number | null,
    quickLinksID: null as number | null,
    showNavigationPrompt: false,
    previousLocation: '',
    listBuilderFilters: null as RequestListBuilderFilter,
    filterPanelDisabled: false,
    listIcon: '',
    listId: '',
    //Set functions
    setNewlyAddedRow: (req: string) => set((state: IStore) => ({ newlyAddedRow: req })),
    // addRequestTitle: (req: any) => set((state: IStore) => ({ requestTitle: req })),
    // addRequestDescription: (req: any) => set((state: IStore) => ({ requestDescription: req })),
    // addRecentData: (req: any) => set((state: IStore) => ({ recentlyAddedData: req })),
    // addRevenueData: (req: any) => set((state: IStore) => ({ impactedRevenue: req })),
    // addDevicesData: (req: any) => set((state: IStore) => ({ impactedDevices: req })),
    // addCommentsData: (req: any) => set((state: IStore) => ({ postCommentsData: [...state.postCommentsData, req] })),
    addFiles: (file: Object[]) => set((state: IStore) => ({ attachedFiles: file })),
    clearFiles: () => set((state: IStore) => ({ attachedFiles: [] as Object[] })),
    // newCommentsData: (req: any) => set((state: IStore) => ({ postCommentsData: req })),
    addSuccessPopUp: (req: boolean) => set((state: IStore) => ({ successPopUp: req })),
    // addCount: (c1: any, c2: any, c3: any) =>
    //     set((state: IStore) => ({
    //         count: { allSDSGCount: c1, allCount: c2, closedAllCount: c3 }
    //     })),
    setRequestData: (req: RequestDto[]) => set((state: IStore) => ({ requestData: req })),
    setQuickLinksFLow: (value: number | null) => set((state: IStore) => ({ quickLinksFLow: value })),

    setRequestType: (req: string) => set((state: IStore) => ({ requestType: req })),
    setRedirectRequestType: (req: string) => set((state: IStore) => ({ redirectRequestType: req })),
    setRedirectSubType: (req: number) => set((state: IStore) => ({ redirectSubType: req })),
    // setSelectionData: (val: any) => set((state: IStore) => ({ selectionData: val })),
    // addTriageModalSuccess: (req: any) => set((state: IStore) => ({ triageModalSuccess: req })),
    addTriageModalSuccessNewModel: (req: boolean) => set((state: IStore) => ({ triageModalSuccessNewModel: req })),
    changeView: (req: string) => set((state: IStore) => ({ view: req })),
    changeCard: (req: string) => set((state: IStore) => ({ card: req })),
    setAccessToken: (token: string) => set((state: IStore) => ({ accessToken: token })),
    setUserInfo: (userInfo: UserDto) => set((state: IStore) => ({ userDetails: userInfo })),
    setMSGraphAccessToken: (token: string) => set((state: IStore) => ({ msGraphAccessToken: token })),
    setAccountDetails: (account: AccountInfo) => set((state: IStore) => ({ accountDetails: account })),
    // setReloadData: (req: any) => set((state: IStore) => ({ reloadData: req })),
    setQuickLinksValue: (val: number | null) => set((state: IStore) => ({ quickLinksValue: val })),
    setQuickLinksID: (val: number | null) => set((state: IStore) => ({ quickLinksID: val })),
    setVisualFlowData: (req: RequestDto) => set((state: IStore) => ({ visualFlowData: req })),
    setUserSearchValue: (value: string) => set((state: IStore) => ({ userSearchValue: value })),
    setAdminTicketsSearchValue: (value: string) => set((state: IStore) => ({ adminTicketsSearchValue: value })),
    setAdminArticlesSearch: (value: string) => set((state: IStore) => ({ adminArticlesSearch: value })),
    setIsAdvancedSearch: (val: boolean) => set((state: IStore) => ({ isAdvancedSearch: val, searchField: val ? 'Default' : 'TitleOnly' })),
    setSearchField: (val: 'Default' | 'TitleOnly') => set((state: IStore) => ({ searchField: val })),
    setSearchInFields: (val: SearchInField[]) => set((state: IStore) => ({ searchInFields: val })),
    setTagSearchType: (val: string | null) => set((state: IStore) => ({ tagSearchType: val })),
    setVisibleColumns: (value: number) => set((state: IStore) => ({ visibleColumns: [...state.visibleColumns, value] })),
    setColumnOrder: (arr: Array<number>) => set((state: IStore) => ({ columnOrder: arr })),
    setColumnsLookUp: (arr: Array<ILookUpProps>) => set((state: IStore) => ({ columnsLookUpData: arr })),
    setSelectedTag: (value: Object) => set((state: IStore) => ({ selectedTag: value })),
    setEditArticleId: (value: Object) => set((state: IStore) => ({ editArticleId: value })),
    setPage: (value: number) => set((state: IStore) => ({ page: value })),
    setArticleCategories: (value: GetArticleCategoryDto[]) => set((state: IStore) => ({ articleCategories: value })),
    setSelectedTab: (val: string) => set((state: IStore) => ({ selectedTab: val })),
    setSearchOrigin: (val: string) => set((state: IStore) => ({ searchOrigin: val })),
    setSearchValueClear: (val: boolean) => set((state: IStore) => ({ searchValueClear: val })),
    setCardTypeFilter: (val: number) => set((state: IStore) => ({ cardTypeFilter: val })),
    setToInitialLookup: () =>
        set((state: IStore) => ({
            columnsLookUpData: [
                {
                    key: 0,
                    column: 'Team',
                    isChecked: true
                },
                {
                    key: 1,
                    column: 'ID',
                    isChecked: true
                },
                {
                    key: 2,
                    column: 'Title',
                    isChecked: true
                },
                {
                    key: 3,
                    column: 'Request Type',
                    isChecked: true
                },
                {
                    key: 4,
                    column: 'Status',
                    isChecked: true
                },
                {
                    key: 5,
                    column: 'Created On',
                    isChecked: true
                },
                {
                    key: 6,
                    column: 'Aging',
                    isChecked: true
                },
                {
                    key: 7,
                    column: 'Total Revenue Risk',
                    isChecked: true
                }
            ]
        })),
    setIsListView: (val: boolean) => set((state: IStore) => ({ isListView: val })),
    setLastSelectedRequest: (val: RequestDto) => set((state: IStore) => ({ lastSelectedRequest: val })),
    // setBoxChange: (val: any) => set((state: IStore) => ({ isBoxchecked: val })),
    setShowNavigationPrompt: (val: boolean) => set((state: IStore) => ({ showNavigationPrompt: val })),
    setOriginalRoutingML : (val: boolean) => set((state: IStore) => ({originalRoutingML : val})),
    setPreviousLocation: (val: string) => set((state: IStore) => ({ previousLocation: val })),
    setListBuilderFilters: (val: RequestListBuilderFilter) => set((state: IStore) => ({ listBuilderFilters: val })),
    setFilterPanelDisabled: (val: boolean) => set((state: IStore) => ({ filterPanelDisabled: val })),
    setListIcon: (val: string) => set((state: IStore) => ({ listIcon: val })),
    setListId: (val: string | number) => set((state: IStore) => ({ listId: val }))
});
const useStore = create(persist(store, { name: 'req' }));
export default useStore;
